import React from 'react';
import { Avatar, CheckIcon, Chip, DeleteIcon, DownloadIcon, Typography, VerifiedUserIcon } from "@orijinworks/frontend-commons"
import { Stack } from "@mui/material";
import AutoDeleteIcon from '@mui/icons-material/AutoDelete';
import { DATE_FORMAT, LOCATIONS } from "../../../../constants";
import { AUDIO_MIME_TYPES, DOCUMENT_MIME_TYPES, EXPORT_LIMIT_EXCEEDED_ERROR_CODE, getFileTypeFilterOptions, getMimeTypesForFileTypeFilter, IMAGE_MIME_TYPES, VIDEO_MIME_TYPES } from '../constants/constants';
import SmsIcon from '@mui/icons-material/Sms';
import moment from "moment";
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SchoolIcon from '@mui/icons-material/School';
import { exportConversationAudit, exportAnnouncementAudit, exportFeedAuditHistory as apiExportFeedAuditHistory, exportAllConversationAsync, exportAllAnnouncementAsync, exportAllFeedHistoryAsync } from "../../../../util/APIUtils";
import MarkChatReadIcon from '@mui/icons-material/MarkChatRead';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import HttpsIcon from '@mui/icons-material/Https';
import momentTZ from "moment-timezone";

const ALL_EXPORT_API_BY_TYPE = {
    Conversations: exportAllConversationAsync,
    Announcements: exportAllAnnouncementAsync,
    'Feed-History': exportAllFeedHistoryAsync,
};


/**
 * Maps learner property names to their corresponding API field names
 */
const LEARNER_PROPERTY_MAPPER = {
    firstName: 'learnerFirstName',
    lastName: 'learnerLastName',
    docId: 'learnerDocId',
    username: 'learnerUsername',
};

/**
 * Maps action type display names to their corresponding API values
 */
const ACTION_TYPE_MAPPER = {
    "Conversation created": "CONVERSATION_CREATED",
    "Conversation enabled": "CONVERSATION_ENABLED",
    "Conversation disabled": "CONVERSATION_DISABLED",
    "Settings updated": "SETTINGS_UPDATED",
};


/**
 * Transforms a statuses filter by splitting comma-separated values into an array
 * @param {Object} filter - Filter object with columnName and value
 * @returns {Object} Transformed filter with value as an array
 */
const transformStatusesFilter = (filter) => {
    return {
        ...filter,
        value: filter.value.split(",").map((v) => v.trim()),
    };
};

/**
 * Transforms an actionTypes filter by mapping display names to API values and splitting into array
 * @param {Object} filter - Filter object with columnName and value
 * @returns {Object} Transformed filter with mapped and split values
 */
const transformActionTypesFilter = (filter) => {
    const mappedValues = filter.value.split(",").map((v) => ACTION_TYPE_MAPPER[v.trim()]);
    return {
        ...filter,
        value: mappedValues,
    };
};

/**
 * Transforms a learner filter by mapping the column name based on preDefinedPropName
 * @param {Object} filter - Filter object with columnName and preDefinedPropName
 * @returns {Object} Transformed filter with updated columnName
 */
const transformLearnerFilter = (filter) => {
    const mappedColumnName = LEARNER_PROPERTY_MAPPER[filter.preDefinedPropName] || filter.columnName;
    return {
        ...filter,
        columnName: mappedColumnName,
    };
};

/**
 * Transforms a fileTypes filter by mapping selected labels to mime types (List<String> for API)
 * @param {Object} filter - Filter object with columnName and value (e.g. "Document, Image")
 * @returns {Object} Transformed filter with value as array of mime type strings
 */
const transformFileTypesFilter = (filter) => {
    const mimeTypes = getMimeTypesForFileTypeFilter(filter.value);
    if (mimeTypes.length === 0) return filter;
    return {
        ...filter,
        value: mimeTypes,
    };
};

/**
 * Filter transformation strategies mapped by column name.
 * For each column, if a strategy exists it is applied; otherwise the filter is returned unchanged.
 */
const FILTER_TRANSFORMATION_STRATEGIES = {
    learner: transformLearnerFilter,
    statuses: transformStatusesFilter,
    actionTypes: transformActionTypesFilter,
    fileTypes: transformFileTypesFilter,
};

const CATEGORY_STYLES = {
    'Image': { backgroundColor: '#E8F5E9', color: '#1B5E20', borderRadius: '4px' },
    'Document': { backgroundColor: '#FFE0B2', color: '#E65100', borderRadius: '4px' },
    'Video': { backgroundColor: '#E3F2FD', color: '#0D47A1', borderRadius: '4px' },
    'Audio': { backgroundColor: '#E1BEE7', color: '#4A148C', borderRadius: '4px' },
    'Other': { backgroundColor: '#E1BEE7', color: '#4A148C', borderRadius: '4px' },
};

/**
 * Categorizes attachments by their mime types and returns counts
 * @param {Array} attachments - Array of attachment objects
 * @returns {Array} Array of categorized chip items
 */
export const categorizeAttachments = (attachments = []) => {
    if (!attachments || attachments.length === 0) return [];

    const counts = {
        'Image': 0,
        'Document': 0,
        'Video': 0,
        'Audio': 0,
        'Other': 0
    };

    attachments.forEach(att => {
        const mime = att.mimeType?.toLowerCase() || '';

        if (IMAGE_MIME_TYPES.includes(mime)) {
            counts['Image']++;
        } else if (VIDEO_MIME_TYPES.includes(mime)) {
            counts['Video']++;
        } else if (AUDIO_MIME_TYPES.includes(mime)) {
            counts['Audio']++;
        } else if (DOCUMENT_MIME_TYPES.includes(mime)) {
            counts['Document']++;
        }
        else {
            counts['Other']++;
        }
    });

    return Object.entries(counts)
        .filter(([_, count]) => count > 0)
        .map(([label, count]) => ({
            label: `${label} (${count})`,
            sx: CATEGORY_STYLES[label] || {}
        }));
};

const getFilter = (filters, columnName) => {
    return filters.find(filter => filter.columnName === columnName);
}

const getAuditFileTypesComponent = (data) => {
    const hasAttachments = data.attachments?.length > 0;
    if (!hasAttachments) {
        return null;
    }
    const greyed = isRowGreyed(data);
    return (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ padding: '4px 0' }}>
            {categorizeAttachments(data.attachments).map((chip, index) => (
                <Chip
                    key={index}
                    label={chip.label}
                    sx={{
                        ...(greyed ? {} : chip.sx),
                        borderRadius: '12px',
                        height: '24px',
                        ...(greyed && {
                            backgroundColor: 'var(--grey-50)',
                            color: 'var(--grey-90)',
                            border: '1px solid var(--grey-60)',
                            '& .MuiChip-label': { color: 'var(--grey-90)' },
                        }),
                        '& .MuiChip-label': {
                            fontSize: '12px',
                            fontWeight: 500
                        }
                    }}
                    size="small"
                />
            ))}
        </Stack>
    );
};

const getAnnouncementStatus = (data) => {
    if (data.isDeleted) {
        return {
            icon: <AutoDeleteIcon sx={{ fontWeight: 'bold', color: isRowGreyed(data) ? 'var(--grey-90)' : "var(--red-main)" }} />,
            text: <Typography sx={{ fontWeight: 'bold', color: isRowGreyed(data) ? 'var(--grey-90)' : "var(--red-main)" }}>Removed</Typography>,
        };
    }
    return {
        icon: <CheckIcon sx={{ fontWeight: 'bold', color: isRowGreyed(data) ? 'var(--grey-90)' : "var(--green-main)" }} />,
        text: <Typography sx={{ fontWeight: 'bold', color: isRowGreyed(data) ? 'var(--grey-90)' : "var(--green-main)" }}>Sent</Typography>,
    };
}

const getRecipientInfoString = (recipients) => {
    if (!recipients || !Array.isArray(recipients)) {
        return '';
    }
    if (recipients.length <= 2) {
        return recipients.map(recipient => recipient.name).join(', ');
    }
    return recipients.slice(0, 2).map(recipient => recipient.name).join(', ') + ' + ' + (recipients.length - 2) + ' more';
}

const isRowGreyed = (data) => {
    return (data.isLocked);
}

const getGreyedCellStyle = (data) => ({
    background: isRowGreyed(data) ? 'var(--grey-20)' : '',
    color: isRowGreyed(data) ? 'var(--grey-90) !important' : '',
});

const getConversationRowStyles = (data) => {
    const cellStyle = getGreyedCellStyle(data);
    return {
        sender: cellStyle,
        recipient: cellStyle,
        isDeleted: cellStyle,
        createdAt: cellStyle,
        message: cellStyle,
        fileTypes: cellStyle,
        actions: cellStyle,
    };
};


/** Builds the conversation row actions array */
const getConversationRowActions = (data, onDeleteConversationHandler, onConversationSettingsUpdateHandler, onViewConversationHandler) => [
    {
        title: 'Go to source',
        onClick: () => {
            if (onViewConversationHandler) {
                onViewConversationHandler(data);
            }
        },
        id: 'source',
        disabled: data.isPrivate === true,
        icon: <SmsIcon sx={{ color: "var(--navy-main)", fontSize: 24 }} />,
        style: { color: "var(--navy-main)" }
    },
    {
        title: 'Remove from Feed',
        onClick: () => onDeleteConversationHandler(data),
        id: 'delete',
        disabled: data.isDeleted,
        icon: <DeleteIcon sx={{ color: "var(--red-dark)", fontSize: 24 }} />,
        style: { color: "var(--red-dark)" }
    },
    {
        title: data.isShutdown ? 'Enable conversation' : 'Shut down conversation',
        onClick: () => onConversationSettingsUpdateHandler(data),
        id: 'settings-update',
        disabled: data.isLocked,
        icon: data.isShutdown ? <LockOpenIcon sx={{ color: "var(--navy-main)", fontSize: 24 }} /> : <LockIcon sx={{ color: "var(--navy-main)", fontSize: 24 }} />,
        style: { color: "var(--navy-main)" }
    }
];

const getAnnouncementActions = (data, onDeleteAnnouncementHandler, onViewAnnouncementHandler) => [
        {
            title: 'Go to source',
            onClick: () => {
                if (onViewAnnouncementHandler) {
                    onViewAnnouncementHandler(data);
                }
            },
            id: 'source',
            disabled: false,
            icon: <SmsIcon sx={{ color: "var(--navy-main)", fontSize: 24 }} />,
        },
        {
            title: 'Remove from Feed',
            onClick: () => onDeleteAnnouncementHandler(data),
            id: 'delete',
            disabled: data.isDeleted,
            icon: <DeleteIcon sx={{ color: "var(--red-dark)", fontSize: 24 }} />,
            style: {
                color: "var(--red-dark)"
            }
        }
    
]

export const mapResponseToAuditConversationData = (data, onDeleteConversationHandler, onConversationSettingsUpdateHandler, onViewConversationHandler) => ({
    rowData: {
        recipient: getRecipientInfoString(data.recipient),
        createdAt: moment(data.createdAt).format('MM/DD/YYYY hh:mm A'),
        message: data.message,
        fileTypes: () => getAuditFileTypesComponent(data),
    },
    styles: getConversationRowStyles(data),
    preDefineComponentProps: {
        sender: {
            icon: data.roleName === 'Learner' ? <SchoolIcon sx={{ color: isRowGreyed(data) ? 'var(--grey-90)' : "var(--navy-main)" }} /> : <VerifiedUserIcon sx={{ color: isRowGreyed(data) ? 'var(--grey-90)' : "var(--navy-main)" }} />,
            text: <Typography variant="body1">{data.senderName ? data.senderName : 'N/A'}</Typography>,
        },
        isDeleted: getAnnouncementStatus(data),
        actions: getConversationRowActions(data, onDeleteConversationHandler, onConversationSettingsUpdateHandler, onViewConversationHandler),
    }
})


export const mapResponseToAuditAnnouncementData = (data, onDeleteAnnouncementHandler, onViewAnnouncementHandler) => ({
    rowData: {
        recipient: getRecipientInfoString(data.membersInfo),
        createdAt: moment(data.createdAt).format('MM/DD/YYYY hh:mm A'),
        subject: data.subject,
        fileTypes: () => getAuditFileTypesComponent(data),
    },
    styles: {
        fileTypes: {
            background: isRowGreyed(data) ? 'var(--grey-20)' : '',
            color: isRowGreyed(data) ? 'var(--grey-90) !important' : '',
        },
    },
    preDefineComponentProps: {
        sender: {
            icon: <VerifiedUserIcon sx={{ color: "var(--navy-main)" }} />,
            text: <Typography variant="body1">{data.senderName ? data.senderName : 'Unknown Admin'}</Typography>,
        },
        isDeleted: getAnnouncementStatus(data),
        actions: getAnnouncementActions(data, onDeleteAnnouncementHandler, onViewAnnouncementHandler),
    }
})

const isFeedHistoryLearnerMember = (member) =>
    member.roleName === 'Learner' ||
    member.roleDisplayName === 'Learner' ||
    Boolean(member.docId);

const findFeedHistoryAdminMember = (members) => {
    const notLearner = (m) => !isFeedHistoryLearnerMember(m);
    return (
        members.find(
            (m) =>
                notLearner(m) &&
                (m.roleName == null || m.roleName === '')
        ) ?? members.find(notLearner)
    );
};

const getFeedHistoryMemberData = (isLearner, members) => {
    let memberData = {
        firstName: 'N/A',
        lastName: 'N/A',
    };
    if (isLearner) {
        const learnerData = members.find(isFeedHistoryLearnerMember);
        if (learnerData) {
            memberData = {
                username: learnerData.username,
                firstName: learnerData.displayFirstName ?? learnerData.firstName,
                lastName: learnerData.displayLastName ?? learnerData.lastName,
                docId: learnerData.docId,
            }
        }
    } else {
        const adminData = findFeedHistoryAdminMember(members);
        if (adminData) {
            memberData = {
                firstName: adminData.displayFirstName ?? adminData.firstName,
                lastName: adminData.displayLastName ?? adminData.lastName,
            }
        }
    }
    return memberData;
};


const getFeedAuditStatus = (status, isLocked) => {
    if (isLocked) {
        return {
            icon: <HttpsIcon sx={{ color: "var(--grey-100)" }} />,
            text: <Typography sx={{ color: "var(--grey-100)", fontWeight: 'bold' }}>Locked</Typography>,
        };
    }
    switch (status) {
        case 'Active':
            return {
                icon: <MarkChatReadIcon sx={{ color: "var(--green-main)" }} />,
                text: <Typography sx={{ color: "var(--green-main)", fontWeight: 'bold' }}>Active</Typography>,
            };
        case 'View Only':
            return {
                icon: <VisibilityIcon sx={{ color: "var(--orange-dark)" }} />,
                text: <Typography sx={{ color: "var(--orange-dark)", fontWeight: 'bold' }}>View Only</Typography>,
            };
        default:
            return;
    }
}

/** Builds the full styles object for feed history audit rows */
const getFeedHistoryRowStyles = (data) => {
    const locked = data.isLocked;
    const cellStyle = locked ? { background: 'var(--grey-20)', color: 'var(--grey-90) !important' } : { background: '', color: '' };
    return {
        learner: {
            ...cellStyle,
            '& *': { color: cellStyle.color }
        },
        adminName: cellStyle,
        statuses: cellStyle,
        dateOfLastAction: cellStyle,
        performedBy: {
            '& *': { color: cellStyle.color },
            ...cellStyle,
        },
        actionTypes: cellStyle,
        actions: cellStyle,
    };
};

/** Builds the feed history row actions array */
const getFeedHistoryRowActions = (data, onConversationSettingsUpdateHandler, onFeedExportHandler) => [
    {
        title: data.permissions.messaging_disabled ? 'Mark as active' : 'Mark as view only',
        onClick: () => onConversationSettingsUpdateHandler({
            ...data,
            isShutdown: data.permissions.messaging_disabled,
        }),
        id: 'settings-update',
        icon: data.permissions.messaging_disabled ? <MarkChatReadIcon sx={{ color: "var(--navy-main)", fontSize: 24 }} /> : <VisibilityIcon sx={{ color: "var(--navy-main)", fontSize: 24 }} />,
        style: { color: "var(--navy-main)" },
        disabled: data.isLocked
    },
    {
        title: 'Export Feed Event History',
        onClick: () => onFeedExportHandler(data),
        id: 'export',
        icon: <DownloadIcon />
    }
];

export const mapResponseToAuditFeedHistoryData = (data, onConversationSettingsUpdateHandler, onFeedExportHandler) => ({
    rowData: {
        adminName: `${getFeedHistoryMemberData(false, data.members).firstName} ${getFeedHistoryMemberData(false, data.members).lastName}`,
        dateOfLastAction: moment.utc(data.dateOfLastAction).format('MM/DD/YYYY hh:mm A'),
        actionTypes: data.lastAction,
    },
    styles: getFeedHistoryRowStyles(data),
    preDefineComponentProps: {
        learner: getFeedHistoryMemberData(true, data.members),
        statuses: getFeedAuditStatus(data.status, data.isLocked),
        performedBy: {
            icon: <Avatar
                firstName={data.performedBy || 'N/A'}
                lastName={data.performedBy || 'N/A'}
                iconSize={52}
                decorative
                avatarProps={{
                    backgroundColor: data.performedBy === 'System' ? "var(--blue-main)" : "var(--green-pale)",
                    textColor: data.performedBy === 'System' ? "var(--white)" : "var(--navy-main)",
                    Icon: <SettingsApplicationsIcon sx={{ color: "white", fontSize: 32 }} />
                }}
                renderState={data.performedBy === 'Orijin System' ? 'icon' : 'initials'} />,
            text: <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>{data.performedBy || 'N/A'}</Typography>,
        },
        actions: getFeedHistoryRowActions(data, onConversationSettingsUpdateHandler, onFeedExportHandler),
    }
})

export const getLocationTransformedFilter = (filters) => {
    const filter = filters.find(filter => filter.columnName === 'location');
    if (filter) {
        const locationNames = filter.value.split(",").map(item => item.trim());
        const facilities = LOCATIONS.filter(item => locationNames.includes(item.locationName) && item.locationTypeId === 3).map(item => item.locationId);
        const HUs = LOCATIONS.filter(item => locationNames.includes(item.locationName) && item.locationTypeId === 15).map(item => item.locationId);
        return {
            facilities,
            HUs,
        };
    }
    return undefined;
}


/**
 * Filter value parsers registry
 * 
 * This registry maps filter types to their corresponding parser functions.
 * Each parser function receives the preprocessed value and returns the parsed value.
 * 
 * To add a new parser for a custom filter type:
 * 1. Add a new entry to this object with the filter type as the key
 * 2. Implement a parser function that takes the value and returns the parsed value
 * 3. The parser will automatically be used when a column has filter.type matching the key
 * 
 * Example:
 * 'custom-type': (value) => {
 *     // Your parsing logic here
 *     return transformedValue;
 * }
 * 
 * Note: Values are preprocessed before being passed to parsers (e.g., '(Blank)' -> '')
 */
const filterValueParsers = {
    /**
     * Parses date range values from comma-separated string to object format
     * @param {string} value - Comma-separated date string (e.g., "2024-01-01, 2024-01-31")
     * @returns {Object|string} Object with startDate and endDate, or original value if invalid
     */
    'date-range': (value) => {
        if (!value || typeof value !== 'string') {
            return value;
        }
        const dates = value.split(',').map(date => date.trim());
        if (dates.length === 2 && dates[0] && dates[1]) {
            return {
                startDate: moment(dates[0]).format(DATE_FORMAT.YYYY_MM_DD),
                endDate: moment(dates[1]).format(DATE_FORMAT.YYYY_MM_DD)
            };
        }
        return value;
    },

    /**
     * Parses multi-tag cell values (typically comma-separated strings or arrays)
     * @param {string|Array} value - Multi-tag value
     * @returns {string|Array} Parsed value (can be extended for specific transformations)
     */
    'MultiTagCell': (value) => {
        // Currently returns as-is, but can be extended for specific transformations
        // Example: if (typeof value === 'string') return value.split(',').map(v => v.trim());
        return value;
    },

    /**
     * Default parser for values that don't need special parsing
     * @param {*} value - Any value
     * @returns {*} Original value
     */
    'default': (value) => value,
};

/**
 * Preprocesses filter value to handle common cases like blank values
 * @param {*} value - Raw filter value
 * @returns {*} Preprocessed value
 */
const preprocessFilterValue = (value) => {
    if (value === '(Blank)') {
        return "";
    }
    return value;
};

/**
 * Gets the appropriate parser for a given filter type
 * @param {string} filterType - Filter type from column definition
 * @returns {Function} Parser function
 */
const getParserForFilterType = (filterType) => {
    return filterValueParsers[filterType] || filterValueParsers['default'];
};

/**
 * Parses filter value based on column filter type
 * @param {*} value - Raw filter value
 * @param {Object} column - Column definition object
 * @returns {*} Parsed filter value
 */
const getParsedFilterValue = (value, column = null) => {
    // Preprocess common cases (e.g., blank values)
    const preprocessedValue = preprocessFilterValue(value);

    // If no column provided, return preprocessed value
    if (!column || !column.filter || !column.filter.type) {
        return preprocessedValue;
    }

    // Get appropriate parser based on filter type
    const parser = getParserForFilterType(column.filter.type);
    return parser(preprocessedValue);
};

export const getFilterValues = (filters = [], columns = []) => {
    return filters.map(filter => {
        const column = columns.find(col => col.name === filter.columnName);
        return {
            [filter.columnName]: getParsedFilterValue(filter.value, column)
        };
    }).reduce((acc, curr) => ({ ...acc, ...curr }), {});
}

const getLocationOptions = (locations) => {
    const locationOptions = [];
    const facilitiesSorted = locations.filter(item => item.locationTypeId === 3).sort((a, b) => a.locationName.localeCompare(b.locationName));
    facilitiesSorted.forEach((facility, index) => {
        locationOptions.push(facility); //DUMPIN FACILITY INTO LOCATION OPTIONS
        const HUs = locations.filter(item => item.parentLocationId === facility.locationId && item.locationTypeId === 15).sort((a, b) => a.locationName.localeCompare(b.locationName));
        locationOptions.push(...HUs);
    });
    return locationOptions.map(item => item.locationName);
};

/**
 * Generates the common base columns for audit tables
 * @param {Array} locations - Array of location objects
 * @returns {Array} Array of common column definitions
 */
const getBaseAuditColumns = (locations) => {
    return [
        {
            name: 'sender',
            preset: 'IconAndTextCell',
            displayName: 'Sender',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'preDefined',
            sortByProp: 'text'
        },
        {
            name: 'recipient',
            displayName: 'Recipient(s)',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'value',
            sortByProp: 'text'
        },
        {
            name: 'createdAt',
            displayName: 'Date',
            sortOrder: 'desc',
            isFilterable: true,
            isSortable: true,
            isVisible: true,
            type: 'value',
            sortByProp: 'createdAt',
            filter: {
                dateFormat: 'M/dd/yyyy',
                type: 'date-range',
                monthsShown: 2
            }
        },
        {
            name: 'isDeleted',
            preset: 'IconAndTextCell',
            displayName: 'Status',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'preDefined',
            sortByProp: 'isDeleted',
            filter: {
                type: 'MultiTagCell'
            },
            options: ["Sent", "Removed"]
        },
        {
            name: 'location',
            displayName: 'Locations',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            hidden: true,
            filter: {
                type: 'MultiTagCell'
            },
            options: getLocationOptions(locations)
        },
        {
            name: 'fileTypes',
            displayName: 'Attachments',
            type: 'override',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            sortOrder: 'none',
            filter: {
                type: 'MultiTagCell',
                showBlankFilter: false,
            },
            options: getFileTypeFilterOptions()
        },
    ];
};

/**
 * Generates audit columns for announcements
 * @param {Array} locations - Array of location objects
 * @returns {Array} Array of column definitions for announcement audit table
 */
export const getAnnouncementAuditColumns = (locations) => {
    const baseColumns = getBaseAuditColumns(locations);
    const contentColumn = {
        name: 'subject',
        displayName: 'Title',
        sortOrder: 'none',
        isFilterable: true,
        isSortable: false,
        isVisible: true,
        type: 'html',
    };

    // Insert content column before location column
    return [
        ...baseColumns.slice(0, -1),
        contentColumn,
        ...baseColumns.slice(-1)
    ];
};

/**
 * Generates audit columns for conversations
 * @param {Array} locations - Array of location objects
 * @returns {Array} Array of column definitions for conversation audit table
 */
export const getConversationAuditColumns = (locations) => {
    const baseColumns = getBaseAuditColumns(locations);
    const contentColumn = {
        name: 'message',
        displayName: 'Message Preview',
        sortOrder: 'none',
        isFilterable: true,
        isSortable: false,
        isVisible: true,
        type: 'html',
    };

    // Insert content column before location column
    return [
        ...baseColumns.slice(0, -1),
        contentColumn,
        ...baseColumns.slice(-1)
    ];
};

/**
 * Generates audit columns for conversations
 * @param {Array} locations - Array of location objects
 * @returns {Array} Array of column definitions for conversation audit table
 */
export const getFeedHistoryAuditColumns = (locations) => {
    return [
        {
            name: 'learner',
            preset: 'LearnerCell',
            displayName: 'Learner',
            filter: {
                type: 'LearnerCell'
            },
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'preDefined',
            sortByProp: 'learnerUserName'
        },
        {
            name: 'adminName',
            displayName: 'Admin',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'value',
            sortByProp: 'text'
        },
        {
            name: 'statuses',
            preset: 'IconAndTextCell',
            displayName: 'Status',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'preDefined',
            sortByProp: 'status',
            filter: {
                type: 'MultiTagCell',
                showBlankFilter: false
            },
            options: ["Active", "Locked", "View Only"]
        },
        {
            name: 'dateOfLastAction',
            displayName: 'Date',
            sortOrder: 'desc',
            isFilterable: true,
            isSortable: true,
            isVisible: true,
            type: 'value',
            sortByProp: 'dateOfLastAction',
            filter: {
                dateFormat: 'M/dd/yyyy',
                type: 'date-range',
                monthsShown: 2
            }
        },
        {
            name: 'actionTypes',
            displayName: 'Last Action',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'value',
            sortByProp: 'text',
            filter: {
                type: 'MultiTagCell',
                showBlankFilter: false
            },
            options: ["Conversation created", "Conversation enabled", "Conversation disabled", "Settings updated"]
        },
        {
            name: 'performedBy',
            preset: 'IconAndTextCell',
            displayName: 'Performed By',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'preDefined',
            sortByProp: 'updatedBy',
            filter: {
                type: 'text'
            }
        },
        {
            name: 'location',
            displayName: 'Locations',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            hidden: true,
            filter: {
                type: 'MultiTagCell'
            },
            options: getLocationOptions(locations)
        },
    ];
};

const highlightSearchString = (string = "", searchString) => {
    if (!string || searchString === "(Blank)") {
        return "";
    }
    return string.replace(new RegExp(searchString, 'gi'), (match) => `<b>${match}</b>`);
};

export const parseAuditLogResponse = (response, filters) => {
    const filter = getFilter(filters, 'subject');
    const searchString = filter ? filter.value : '';
    response.data.content = response.data.content.map((_data) => {
        _data.subject = highlightSearchString(_data.subject, searchString);
        return _data;
    });
    return response;
}


export const parseConversationAuditLogResponse = (response, filters) => {
    const filter = getFilter(filters, "message");
    if (filter) {
        const searchString = filter ? filter.value : '';
        response.data.content = response.data.content.map((_data) => {
            _data.message = highlightSearchString(_data.message, searchString);
            return _data;
        });
    }
    return response;
}

export const getIsDeletedFilter = (filters) => {
    const filter = getFilter(filters, 'isDeleted');
    if (filter) {
        if (filter.value.indexOf("Sent") > -1 && filter.value.indexOf("Removed") > -1) {
            return undefined;
        } else if (filter.value.indexOf("Sent") > -1) {
            return false;
        } else if (filter.value.indexOf("Removed") > -1) {
            return true;
        }
    }
    return undefined;
}

/**
 * Normalizes audit dependencies for comparison
 * Creates a serializable object from audit hook dependencies
 * @param {number} currentPage - Current page number
 * @param {number} pageSize - Page size
 * @param {string} sortColumn - Column to sort by
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @param {string} selectedType - Selected audit type
 * @param {Array} filter - Filter array
 * @returns {Object} Normalized dependency object
 */
export const normalizeAuditDependencies = (currentPage, pageSize, sortColumn, sortDirection, selectedType, filter) => {
    return {
        currentPage,
        pageSize,
        sortColumn,
        sortDirection,
        selectedType,
        filterLength: filter.length,
        filterString: JSON.stringify(filter)
    };
}

/**
 * Checks if audit dependencies have changed
 * Compares current dependencies with previous dependencies
 * @param {Object} currentDeps - Current normalized dependencies
 * @param {Object} prevDeps - Previous normalized dependencies (can be empty object)
 * @returns {boolean} True if dependencies changed, false otherwise
 */
export const hasAuditDependenciesChanged = (currentDeps, prevDeps) => {
    const currentDepsString = JSON.stringify(currentDeps);
    const prevDepsString = JSON.stringify(prevDeps || {});
    return currentDepsString !== prevDepsString;
}

const getDocId = (docId) => {
    return docId ? `(${docId})` : '';
};

export const updateConversationAuditLogResponse = (response) => {
    response.data.content = response.data.content.map((_data) => {
        const isPrivate = _data.messagePreview === null || _data.messagePreview === undefined;
        return {
            senderName: `${_data.sender.name} ${getDocId(_data.sender.docId)}`,
            recipient: _data.recipients.map(item => ({ name: item })),
            createdAt: _data.dateSent,
            isDeleted: _data.status === 'DELETED',
            message: isPrivate ? 'Private' : _data.messagePreview,
            feedId: _data.feedId,
            id: _data.messageId,
            isShutdown: _data.isShutdown,
            attachments: _data.attachments || [],
            roleName: _data.sender.roleName,
            isPrivate: isPrivate, // Flag to indicate if this is a private/confidential conversation,
            isLocked: _data.isLocked,
        };
    });
    return response;
}

/**
 * Builds export parameters for conversation audit export
 * @param {number} currentPage - Current page number
 * @param {number} pageSize - Page size
 * @param {string} sortColumn - Column to sort by
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @param {Array} filter - Filter array
 * @param {Array} columns - Column definitions (for parsing filter types)
 * @returns {Object} Export parameters object
 */
export const buildConversationExportParams = (currentPage, pageSize, sortColumn, sortDirection, filter, columns = []) => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return {
        page: currentPage,
        size: pageSize,
        sort: sortColumn + ',' + sortDirection,
        ...getFilterValues(transformFeedHistoryFilterProperties(filter), columns),
        locationIds: getLocationTransformedFilter(filter),
        timezone: userTimezone,
    };
}

/**
 * Builds export parameters for announcement audit export
 * @param {number} currentPage - Current page number
 * @param {number} pageSize - Page size
 * @param {string} sortColumn - Column to sort by
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @param {Array} filter - Filter array
 * @param {Array} columns - Column definitions (for parsing filter types)
 * @returns {Object} Export parameters object
 */
export const buildAnnouncementExportParams = (currentPage, pageSize, sortColumn, sortDirection, filter, columns = []) => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const filterValues = getFilterValues(transformFeedHistoryFilterProperties(filter), columns);
    const locationFilters = getLocationTransformedFilter(filter);
    const isDeleted = getIsDeletedFilter(filter);

    return {
        page: currentPage,
        size: pageSize,
        sort: sortColumn + ',' + sortDirection,
        ...filterValues,
        facilities: locationFilters?.facilities,
        HUs: locationFilters?.HUs,
        isDeleted: isDeleted,
        timezone: userTimezone,
    };
}

/**
 * Builds export URL with query parameters for audit export endpoints
 * @param {string} baseUrl - The base URL for the export endpoint
 * @param {Object} params - Parameters object containing page, size, sort, and timezone
 * @returns {string} The complete URL with query parameters
 */
export const buildExportUrl = (baseUrl, params = {}) => {
    const { page, size, sort, timezone } = params;
    const queryParams = [];
    if (page !== undefined) queryParams.push(`page=${page - 1 || 0}`);
    if (size !== undefined) queryParams.push(`size=${size || 10}`);
    if (sort !== undefined) queryParams.push(`sort=${sort || 'createdAt,desc'}`);
    if (timezone !== undefined) queryParams.push(`timezone=${encodeURIComponent(timezone)}`);

    return queryParams.length > 0
        ? `${baseUrl}?${queryParams.join('&')}`
        : baseUrl;
}

/**
 * Generic function to download a blob as a file with the specified filename
 * @param {Blob} blob - The blob to download
 * @param {string} filename - The filename for the downloaded file
 */
export const downloadBlobAsFile = (blob, filename) => {
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

/**
 * Downloads a blob as a CSV file with the specified filename
 * @param {Blob} blob - The blob to download
 * @param {string} typeName - The type name for the filename (e.g., 'Conversations', 'Announcements')
 * @param {number} pageNumber - The current page number for the filename
 */
export const downloadExportFile = (blob, typeName, pageNumber, fileName) => {
    // Generate filename: YYYY-MM-DD_TypeName_PageNumber.csv
    const dateStr = new Date().toISOString().split('T')[0];
    fileName = fileName || `${dateStr}_${typeName}_${pageNumber}.csv`;
    downloadBlobAsFile(blob, fileName);
}

/**
 * Exports audit data based on the selected type
 * Handles the complete export flow: building params, calling API, and downloading file
 * @param {string} selectedType - The selected audit type ('Conversations' or 'Announcements')
 * @param {number} currentPage - Current page number
 * @param {number} pageSize - Page size
 * @param {string} sortColumn - Column to sort by
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @param {Array} filter - Filter array
 * @param {Array} columns - Column definitions (for filter parsing)
 * @returns {Promise<Blob>} The exported blob
 * @throws {Error} If export fails or invalid type is provided
 */
export const exportAuditData = async (selectedType, currentPage, pageSize, sortColumn, sortDirection, filter, columns = []) => {
    let exportParams;
    let blob;

    if (selectedType === 'Conversations') {
        exportParams = buildConversationExportParams(currentPage, pageSize, sortColumn, sortDirection, filter, columns);
        blob = await exportConversationAudit(exportParams);
        downloadExportFile(blob, 'Conversations', currentPage);
        return blob;
    } else if (selectedType === 'Announcements') {
        exportParams = buildAnnouncementExportParams(currentPage, pageSize, sortColumn, sortDirection, filter, columns);
        blob = await exportAnnouncementAudit(exportParams);
        downloadExportFile(blob, 'Announcements', currentPage);
        return blob;
    } else {
        throw new Error(`Invalid selectedType: ${selectedType}. Expected 'Conversations' or 'Announcements'`);
    }
}


/**
 * Exports audit data based on the selected type
 * Handles the complete export flow: building params, calling API, and downloading file
 * @param {string} selectedType - The selected audit type ('Conversations' or 'Announcements')
 * @param {number} currentPage - Current page number
 * @param {number} pageSize - Page size
 * @param {string} sortColumn - Column to sort by
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @param {Array} filter - Filter array
 * @returns {Promise<Blob>} The exported blob
 * @throws {Error} If export fails or invalid type is provided
 */
export const exportFeedAuditHistory = async (feedId, feedName) => {
    const blob = await apiExportFeedAuditHistory(feedId)
    // Sanitize feed name for filename (replace spaces and special chars with underscores)
    const sanitizedFeedName = feedName ? feedName.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') : 'Unknown';
    const formattedCSVFileName = `${moment().format('YYYY-MM-DD')}_FeedEventHistory_${sanitizedFeedName}.csv`;
    downloadExportFile(blob, formattedCSVFileName, 0, formattedCSVFileName);
    return blob;
}


/**
 * Transforms feed history filter properties to match API requirements
 * Reduces cyclomatic complexity by using a strategy pattern instead of if-else chains
 * @param {Array} filters - Array of filter objects
 * @returns {Array} Array of transformed filter objects
 */
export const transformFeedHistoryFilterProperties = (filters) => {
    return filters.map((filter) => {
        const transformationStrategy = FILTER_TRANSFORMATION_STRATEGIES[filter.columnName];
        return transformationStrategy ? transformationStrategy(filter) : filter;
    });
};

/**
 * Extracts and formats date range filter from filters array
 * @param {Array} filters - Array of filter objects
 * @returns {Object} Object with startDate and endDate in YYYY-MM-DD format, or empty object if not found
 */
export const getDateRangeFilter = (filters) => {
    const dateFilter = filters.find((f) => f.columnName === 'dateOfLastAction');
    if (!dateFilter) {
        return {};
    }

    const [startDate, endDate] = dateFilter.value.split(",").map((date) => date.trim());
    return {
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
    };
}



/**
 * Builds export params for "export all" by selected audit type
 * @param {string} selectedType - 'Conversations' or 'Announcements'
 * @param {*} emails - Emails for export
 * @param {string} password - Password for export
 * @param {Array} filter - Filter array
 * @param {Array} columns - Column definitions
 * @returns {Object} Params for the export API
 */
const buildAllExportParams = (selectedType, emails, password, filter, columns) => {
    const transformedFilter = transformFeedHistoryFilterProperties(filter);
    const base = {
        emails,
        password,
        timezone: momentTZ.tz.guess(),
        ...getFilterValues(transformedFilter, columns),
        isDeleted: getIsDeletedFilter(filter),
    };
    const locationFilter = getLocationTransformedFilter(filter);
    return selectedType === 'Conversations'
        ? { ...base, locationIds: locationFilter }
        : { ...base, ...locationFilter };
};

/**
 * Params for feed history async export; filter matches POST /api/feeds/audit-log body shape.
 */
const buildFeedHistoryAllExportParams = (emails, password, filter, columns) => {
    const transformedFilter = transformFeedHistoryFilterProperties(filter);
    const filterValues = getFilterValues(transformedFilter, columns);
    const restFilterValues = { ...filterValues };
    delete restFilterValues.dateOfLastAction;
    const dateRange = getDateRangeFilter(filter);
    const locationFilter = getLocationTransformedFilter(filter);
    const locationKeys = locationFilter
        ? [...(locationFilter.facilities || []), ...(locationFilter.HUs || [])]
        : [];
    const apiFilter = { ...restFilterValues, ...dateRange };
    if (locationKeys.length > 0) {
        apiFilter.locationKeys = locationKeys;
    }
    return {
        emails,
        password,
        timezone: momentTZ.tz.guess(),
        filter: apiFilter,
    };
};

/**
 * Exports all audit data for the selected type (Conversations or Announcements)
 * @param {string} selectedType - The selected audit type
 * @param {*} emails - Emails for export
 * @param {string} password - Password for export
 * @param {Array} filter - Filter array
 * @param {Array} columns - Column definitions
 * @returns {Promise<string>} Resolves with 'Success' on completion
 * @throws {Error} If selectedType is invalid or export fails
 */
/**
 * @returns {Promise<{ success: true }|{ success: false, errorCode: string, message?: string }>}
 * Success: { success: true }. Limit exceeded: { success: false, errorCode: EXPORT_LIMIT_EXCEEDED_ERROR_CODE, message }.
 */
export const exportAllDataAsync = async (selectedType, emails, password, filter, columns) => {
    const apiCall = ALL_EXPORT_API_BY_TYPE[selectedType];
    if (!apiCall) {
        throw new Error(`Invalid selectedType: ${selectedType}. Expected 'Conversations', 'Announcements', or 'Feed-History'`);
    }
    const params = selectedType === 'Feed-History'
        ? buildFeedHistoryAllExportParams(emails, password, filter, columns)
        : buildAllExportParams(selectedType, emails, password, filter, columns);
    const response = await apiCall(params);
    // API returns { status, message, data: { errorCode, message }, timestamp }; support gateway-wrapped { data: <body> } too
    const errorPayload = response?.data ?? response;
    const errorCode = errorPayload?.errorCode ?? errorPayload?.data?.errorCode;
    const errorMessage = errorPayload?.message ?? errorPayload?.data?.message;
    if (errorCode === EXPORT_LIMIT_EXCEEDED_ERROR_CODE) {
        return { success: false, errorCode, message: errorMessage };
    }
    return { success: true };
}